import React, { useState } from 'react';
import { goldButton } from '../../PageShell';
import { cfgInput, parseTags, uploadProductImage } from './productUtils';

const arrowBtn = { background: 'none', border: '1px solid rgba(201,168,76,0.35)', color: '#C9A84C', width: '30px', height: '30px', fontSize: '13px', cursor: 'pointer', lineHeight: 1 };

const EditPanel = ({ p, onSave, setMsg }) => {
  const [form, setForm] = useState({
    name_es: p.name?.es || '',
    description_es: p.description?.es || '',
    category_es: p.category?.es || '',
    image: p.image || '',
    tags: (p.tags || []).join(', '),
    discount_pct: String(p.discount_pct || 0),
    active: p.active !== false,
  });
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setMsg('');
    try {
      const url = await uploadProductImage(file);
      setForm((f) => ({ ...f, image: url }));
      setMsg('✓ Imagen subida.');
    } catch (e) {
      setMsg('Error al subir imagen: ' + (e.response?.data?.detail || ''));
    } finally {
      setUploading(false);
    }
  };

  const save = () => {
    if (!form.name_es.trim()) { setMsg('El nombre no puede estar vacío.'); return; }
    onSave({
      name_es: form.name_es.trim(),
      description_es: form.description_es,
      category_es: form.category_es || 'Otros',
      image: form.image,
      tags: parseTags(form.tags),
      discount_pct: Math.min(90, Math.max(0, Number(form.discount_pct || 0))),
      active: form.active,
    }, form.name_es);
  };

  return (
    <div className="p-3" style={{ borderTop: '1px dashed rgba(201,168,76,0.25)' }} data-testid={`product-edit-panel-${p.id}`}>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="font-body" style={{ fontSize: '10px', color: '#C2C1B6' }}>Nombre
          <input value={form.name_es} onChange={(e) => setForm({ ...form, name_es: e.target.value })} style={cfgInput} data-testid="edit-name" />
        </label>
        <label className="font-body" style={{ fontSize: '10px', color: '#C2C1B6' }}>Categoría
          <input value={form.category_es} onChange={(e) => setForm({ ...form, category_es: e.target.value })} style={cfgInput} data-testid="edit-category" />
        </label>
        <label className="font-body sm:col-span-2" style={{ fontSize: '10px', color: '#C2C1B6' }}>Descripción
          <textarea rows={2} value={form.description_es} onChange={(e) => setForm({ ...form, description_es: e.target.value })} style={{ ...cfgInput, resize: 'vertical' }} data-testid="edit-description" />
        </label>
        <label className="font-body" style={{ fontSize: '10px', color: '#C2C1B6' }}>URL de imagen
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} style={cfgInput} data-testid="edit-image" />
          <span className="block mt-1">
            <input type="file" accept="image/*" id={`edit-file-${p.id}`} style={{ display: 'none' }} onChange={(e) => upload(e.target.files[0])} data-testid="edit-image-file" />
            <label htmlFor={`edit-file-${p.id}`} className="font-body" style={{ color: '#C9A84C', border: '1px solid rgba(201,168,76,0.4)', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', display: 'inline-block' }}>
              {uploading ? 'Subiendo…' : '📷 Subir desde fotos / archivos'}
            </label>
          </span>
        </label>
        <label className="font-body" style={{ fontSize: '10px', color: '#C2C1B6' }}>Tags (separados por coma)
          <input placeholder="Nuevo, Picante, Vegano" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} style={cfgInput} data-testid="edit-tags" />
        </label>
        <label className="font-body" style={{ fontSize: '10px', color: '#C2C1B6' }}>Descuento % (0 = sin descuento)
          <input type="number" min="0" max="90" step="0.5" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} style={cfgInput} data-testid="edit-discount" />
        </label>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <label className="font-body flex items-center gap-2" style={{ fontSize: '12px', color: '#C2C1B6', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="edit-active" />
          Visible en el menú
        </label>
        <button onClick={save} style={{ ...goldButton, padding: '8px 18px' }} data-testid="edit-save">Guardar cambios</button>
      </div>
    </div>
  );
};

export const ProductRow = ({ p, index, count, editing, onToggleEdit, onMove, onFieldChange, onSaveInline, onSaveEdit, onDelete, setMsg }) => (
  <div style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.14)' }} data-testid={`product-${p.id}`}>
    <div className="flex items-center gap-2 flex-wrap p-3">
      <div className="flex flex-col" style={{ gap: '2px' }}>
        <button onClick={() => onMove(p, 'up')} disabled={index === 0} style={{ ...arrowBtn, opacity: index === 0 ? 0.3 : 1 }} data-testid={`product-move-up-${p.id}`} title="Subir">↑</button>
        <button onClick={() => onMove(p, 'down')} disabled={index === count - 1} style={{ ...arrowBtn, opacity: index === count - 1 ? 0.3 : 1 }} data-testid={`product-move-down-${p.id}`} title="Bajar">↓</button>
      </div>
      <span className="font-body" style={{ flex: '1 1 160px', fontSize: '13px', color: p.active === false ? '#7A7A70' : '#F5F5F0' }}>
        {p.name.es}
        <span style={{ color: '#9A7522', fontSize: '11px' }}> · {p.category?.es}</span>
        {p.active === false && <span style={{ color: '#E0736F', fontSize: '10px' }}> · oculto</span>}
        {(p.discount_pct || 0) > 0 && <span style={{ color: '#E0736F', fontSize: '10px', fontWeight: 700 }}> · -{p.discount_pct}%</span>}
        {(p.tags || []).length > 0 && (
          <span className="block mt-1">
            {p.tags.map((t) => (
              <span key={t} className="font-body uppercase" style={{ fontSize: '9px', letterSpacing: '1px', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.4)', padding: '1px 6px', marginRight: '4px' }}>{t}</span>
            ))}
          </span>
        )}
      </span>
      <label style={{ fontSize: '10px', color: '#C2C1B6' }}>
        $
        <input
          type="number"
          step="0.01"
          min="0"
          value={p.price}
          onChange={(e) => onFieldChange(p.id, 'price', e.target.value)}
          style={{ ...cfgInput, width: '80px', display: 'inline-block', marginLeft: '4px' }}
          data-testid={`product-price-${p.id}`}
        />
      </label>
      <label style={{ fontSize: '10px', color: '#C2C1B6' }}>
        Tax %
        <input
          type="number"
          step="0.1"
          min="0"
          value={p._tax_pct}
          onChange={(e) => onFieldChange(p.id, '_tax_pct', e.target.value)}
          style={{ ...cfgInput, width: '70px', display: 'inline-block', marginLeft: '4px' }}
          data-testid={`product-tax-${p.id}`}
        />
      </label>
      <button onClick={() => onSaveInline(p)} style={{ ...goldButton, padding: '7px 14px' }} data-testid={`product-save-${p.id}`}>
        Guardar
      </button>
      <button
        onClick={onToggleEdit}
        className="font-body"
        style={{ fontSize: '12px', color: '#C9A84C', background: 'none', border: '1px solid rgba(201,168,76,0.4)', padding: '7px 12px', cursor: 'pointer' }}
        data-testid={`product-edit-${p.id}`}
      >
        {editing ? 'Cerrar' : 'Editar'}
      </button>
      <button
        onClick={() => onDelete(p)}
        className="font-body"
        style={{ fontSize: '12px', color: '#E0736F', background: 'none', border: '1px solid rgba(224,115,111,0.4)', padding: '7px 12px', cursor: 'pointer' }}
        data-testid={`product-delete-${p.id}`}
      >
        Eliminar
      </button>
    </div>

    {editing && <EditPanel p={p} onSave={onSaveEdit} setMsg={setMsg} />}
  </div>
);
